import React, { useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ScrollingNotification } from '../../components/notifications/ScrollingNotification';
import { useInterviewRailMessages } from '../../hooks/useInterviewRailNotifications';
import { Routes } from '../../routes';
import { useAppSelector } from '../../stores';

export type ContributorGuideSection = {
    id: string;
    name: string;
    objective?: string;
    kept_question_count: number;
    answered_count: number;
    step_status: 'complete' | 'current' | 'upcoming';
};

export type ContributorGuideProgress = {
    sections: ContributorGuideSection[];
    answered_kept_total: number;
    kept_total: number;
    current_question_number?: number;
    current_section_name?: string | null;
    role_label?: string | null;
};

const EMPTY_SECTIONS: ContributorGuideSection[] = [];

function stepDotClass(status: ContributorGuideSection['step_status']): string {
    if (status === 'complete') return 'sd-done';
    if (status === 'current') return 'sd-active';
    return 'sd-pending';
}

export interface ContributorRightPanelProps {
    guideProgress: ContributorGuideProgress | null;
    exportLoading: boolean;
    onExport: () => void | Promise<void>;
    /** API : l’e-mail invité existe dans Users — sinon afficher « Créer un compte » même avec une autre session Redux */
    inviteEmailHasFamlaAccount?: boolean;
    /**
     * Anciennes props (aperçu propriétaire) — conservées pour compatibilité bundler / HMR.
     * Ne plus utiliser : le panneau droit est aligné sur la page contributeur.
     */
    recipientPreviewMode?: boolean;
    previewWorkspaceHref?: string;
}

function ContributorRightPanel({
    guideProgress,
    exportLoading,
    onExport,
    inviteEmailHasFamlaAccount,
    recipientPreviewMode = false,
    previewWorkspaceHref,
}: ContributorRightPanelProps) {
    void recipientPreviewMode;
    void previewWorkspaceHref;
    const { t } = useTranslation();
    const railNotificationMessages = useInterviewRailMessages();
    const accessToken = useAppSelector((state) => state.auth.access_token);
    const hasBrowserFamlaSession = Boolean(accessToken);
    const showCreateFamlaAccount =
        inviteEmailHasFamlaAccount === false ||
        (inviteEmailHasFamlaAccount === undefined && !hasBrowserFamlaSession);
    const showNewDataCollection = hasBrowserFamlaSession && inviteEmailHasFamlaAccount !== false;

    const sections = guideProgress?.sections ?? EMPTY_SECTIONS;
    /** Étapes = sections du guide ; le détail par question reste sous chaque titre de section */
    const { stepsDone, stepsTotal } = useMemo(() => {
        const sec = guideProgress?.sections;
        if (sec && sec.length > 0) {
            let done = 0;
            for (const s of sec) {
                const nk = Number(s.kept_question_count) || 0;
                const na = Number(s.answered_count) || 0;
                if (nk > 0 && na >= nk) done += 1;
            }
            return { stepsDone: done, stepsTotal: sec.length };
        }
        const qk = guideProgress?.kept_total ?? 0;
        const qa = guideProgress?.answered_kept_total ?? 0;
        return {
            stepsDone: qk > 0 && qa >= qk ? 1 : 0,
            stepsTotal: qk > 0 ? 1 : 0,
        };
    }, [guideProgress]);

    const pct = useMemo(() => {
        if (stepsTotal <= 0) return 0;
        return Math.min(100, Math.round((stepsDone / stepsTotal) * 100));
    }, [stepsDone, stepsTotal]);

    const sectionDetail = (s: ContributorGuideSection): string => {
        const n = s.kept_question_count;
        const a = s.answered_count;
        if (s.step_status === 'complete') {
            return t('form.collectWorkspace.contributorSectionCompleted', {
                count: n,
                defaultValue: '{{count}} questions · completed',
            });
        }
        if (s.step_status === 'current') {
            if (a > 0 && a < n) {
                return t('form.collectWorkspace.contributorSectionPartial', {
                    answered: a,
                    total: n,
                    defaultValue: '{{answered}} / {{total}} questions · in progress',
                });
            }
            return t('form.collectWorkspace.inProgress', 'In progress');
        }
        return t('form.collectWorkspace.contributorStatusNotStarted', 'Not started');
    };

    const signupUrl = useMemo(() => `${window.location.origin}${Routes.AUTH_SIGNIN}`, []);

    const recommendFamla = useCallback(async () => {
        const title = t('form.collectWorkspace.contributorRecommendShareTitle', '{{appName}}');
        const text = t('form.collectWorkspace.contributorRecommendShareText', {
            url: signupUrl,
            defaultValue: 'Try {{appName}} for qualitative research and interviews: {{url}}',
        });
        try {
            if (typeof navigator !== 'undefined' && navigator.share) {
                await navigator.share({ title, text, url: signupUrl });
                return;
            }
        } catch (e: unknown) {
            const err = e as { name?: string };
            if (err?.name === 'AbortError') return;
        }
        const subject = encodeURIComponent(
            t('form.collectWorkspace.contributorRecommendEmailSubject', {
                defaultValue: 'I thought you might like {{appName}}',
            })
        );
        const body = encodeURIComponent(
            t('form.collectWorkspace.contributorRecommendEmailBody', {
                url: signupUrl,
                defaultValue:
                    "Hi,\n\nI've been using {{appName}} for interviews and qualitative research — here's where to sign up:\n{{url}}\n",
            })
        );
        window.location.href = `mailto:?subject=${subject}&body=${body}`;
    }, [signupUrl, t]);

    return (
        <div className="right-panel collect-contrib-right">
            <div className="rp-scroll">
                {sections.length > 0 ? (
                    <div className="rp-sec">
                        <div className="rp-title-row">
                            <div className="rp-title">
                            {t('form.collectWorkspace.contributorYourProgress')}
                            </div>
                        </div>
                        <div className="prog-bar-row collect-contrib-prog-bar-row">
                            <div className="prog-track">
                                <div className="prog-fill" style={{ width: `${pct}%` }} />
                            </div>
                            <span
                                className="prog-pct collect-contrib-prog-count"
                                aria-live="polite"
                                title={t('form.collectWorkspace.contributorProgressStepsHint', {
                                    done: stepsDone,
                                    total: stepsTotal,
                                    defaultValue: '{{done}} of {{total}} steps completed',
                                })}
                            >
                                {t('form.collectWorkspace.contributorProgressStepCount', {
                                    done: stepsDone,
                                    total: stepsTotal,
                                    defaultValue: '{{done}} / {{total}}',
                                })}
                            </span>
                        </div>

                        {sections.map((item, index) => {
                            const isDone = item.step_status === 'complete';
                            const dotClass = stepDotClass(item.step_status);
                            const nameClass = item.step_status === 'upcoming' ? 'step-name muted' : 'step-name';
                            const showLine = index < sections.length - 1;
                            const detail = sectionDetail(item);

                            return (
                                <React.Fragment key={item.id}>
                                    <div className="p-step">
                                        <div className={`step-dot ${dotClass}`}>
                                            {isDone ? <span className="step-check" aria-hidden>✓</span> : null}
                                            {item.step_status === 'current' ? (
                                                <span className="collect-contrib-step-warn" aria-hidden>
                                                    !
                                                </span>
                                            ) : null}
                                        </div>
                                        <div className="step-info">
                                            <div className={nameClass}>{item.name}</div>
                                            
                                        </div>
                                    </div>
                                    {showLine ? <div className="step-line" /> : null}
                                </React.Fragment>
                            );
                        })}
                    </div>
                ) : null}

                <div className="rp-sec rp-sec--quick">
                    <div className="rp-title">{t('words.quickActions')}</div>
                    {showNewDataCollection ? (
                        <Link to="/process/data-collect/create" className="qa-row collect-contrib-qa-link">
                            <svg style={{ width: 12, height: 12 }} viewBox="0 0 16 16" fill="none" aria-hidden>
                                <path d="M8 2V14M2 8H14" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                            </svg>
                            {t('form.collectWorkspace.newDataCollection', 'New data collection')}
                        </Link>
                    ) : null}
                    <button type="button" className="qa-row sec" onClick={() => void onExport()} disabled={exportLoading}>
                        <svg style={{ width: 12, height: 12 }} viewBox="0 0 16 16" fill="none" aria-hidden>
                            <path
                                d="M8 3V9M5 6L8 3L11 6"
                                stroke="currentColor"
                                strokeWidth="1.2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                            <path d="M3 11V12C3 12.6 3.4 13 4 13H12C12.6 13 13 12.6 13 12V11" stroke="currentColor" strokeWidth="1.2" />
                        </svg>
                        {exportLoading ? t('words.loading', 'Loading…') : t('form.collectWorkspace.exportTranscript', 'Export my transcript')}
                    </button>
                    {showCreateFamlaAccount ? (
                        <Link to={Routes.AUTH_SIGNIN} className="qa-row collect-contrib-qa-link">
                            <svg style={{ width: 12, height: 12 }} viewBox="0 0 24 24" fill="none" aria-hidden>
                                <path
                                    d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                                <circle cx="8.5" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
                                <path d="M20 8v6M17 11h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                            {t('form.collectWorkspace.contributorQuickCreateAccount', 'Create a {{appName}} account')}
                        </Link>
                    ) : null}
                    <button type="button" className="qa-row sec" onClick={() => void recommendFamla()}>
                        <svg style={{ width: 12, height: 12 }} viewBox="0 0 24 24" fill="none" aria-hidden>
                            <path
                                d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                        {t('form.collectWorkspace.contributorQuickRecommendFriend', 'Recommend {{appName}} to a friend')}
                    </button>
                </div>
            </div>

            <div className="rp-announce-wrap">
                <ScrollingNotification
                    className="rp-scrolling-notification"
                    messages={railNotificationMessages}
                    onDismiss={() => undefined}
                    onClick={() => undefined}
                />
            </div>
        </div>
    );
}

export default ContributorRightPanel;
